import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import io from 'socket.io-client';
import { store } from '../../../redux/oldStore';
import { ServerResponse } from '../../../utils/Server';
import Icon, { IconType } from '../../Icon/Icon';
import Modal from '../../Modal/Modal';
import {
  ICaptainPortalAssignment,
  ICaptainPortalData,
  ICaptainPortalJudge,
  ICaptainPortalProject,
  IScoreSubmittedPayload,
} from './CategoryCaptainPortalTypes';
import './CategoryCaptainPortal.css';

const formatScore = (score: number | null | undefined) => {
  if (score == null || Number.isNaN(score)) return '';
  return score.toFixed(3);
};

const CategoryCaptainPortal = () => {
  const [data, setData] = useState<ICaptainPortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [projectKey, setProjectKey] = useState('0');
  const [judgeId, setJudgeId] = useState('0');
  const [sessionId, setSessionId] = useState('0');
  const [saving, setSaving] = useState(false);
  const dataRef = useRef<ICaptainPortalData | null>(null);
  dataRef.current = data;

  const load = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    store.server
      .postApi<ServerResponse<ICaptainPortalData>>('../JudgingCaptain/PortalData', {})
      .then((x) => {
        if (x.Success && x.Value) {
          setData(x.Value);
        } else if (!opts?.silent) {
          toast.error(x.Message || 'Unable to load captain portal.', { autoClose: false });
          setData(null);
        }
      })
      .catch(() => {
        if (!opts?.silent) {
          toast.error('Unable to load captain portal.', { autoClose: false });
          setData(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fairId = data?.fairId;
    const clientId = data?.clientId;
    if (!fairId || !clientId) return;

    const url =
      window.location.host.indexOf('localhost') > -1
        ? 'http://localhost:9191/online-judging'
        : 'https://socket.zfairs.com/online-judging';
    const s = io(url, {
      query: {
        fairId,
        clientId,
        captain: true,
      },
      transports: ['websocket', 'polling'],
    });

    const onScoreSubmitted = (payload: IScoreSubmittedPayload) => {
      const current = dataRef.current;
      if (!current) return;

      const projectKey = (payload?.projectKey || '').toLowerCase();
      const projectId = payload?.projectId;
      const projectIntId = payload?.projectIntId;
      const matches = (current.projects ?? []).some(
        (p) =>
          (projectIntId != null && p.projectIntId === projectIntId) ||
          (projectKey && (p.projectKey || '').toLowerCase() === projectKey) ||
          (projectId && p.projectId === projectId)
      );
      if (!matches) return;

      const who = payload?.judgeName || 'A judge';
      const entry = projectId || payload?.title || 'an entry';
      toast.info(
        payload?.noShow ? `${who} marked ${entry} as a no-show.` : `${who} submitted a score for ${entry}.`
      );
      load({ silent: true });
    };

    s.on('score-submitted', onScoreSubmitted);

    return () => {
      s.off('score-submitted', onScoreSubmitted);
      s.close();
    };
  }, [data?.fairId, data?.clientId, load]);

  const assignmentsByProject = useMemo(() => {
    const map: Record<number, ICaptainPortalAssignment[]> = {};
    (data?.assignments ?? []).forEach((a) => {
      if (!map[a.projectIntId]) map[a.projectIntId] = [];
      map[a.projectIntId].push(a);
    });
    return map;
  }, [data]);

  const assignmentsByJudge = useMemo(() => {
    const map: Record<number, ICaptainPortalAssignment[]> = {};
    (data?.assignments ?? []).forEach((a) => {
      if (!map[a.judgeId]) map[a.judgeId] = [];
      map[a.judgeId].push(a);
    });
    return map;
  }, [data]);

  const toggleFlag = (project: ICaptainPortalProject) => {
    const next = !project.flagged;
    const label = next ? 'flag' : 'unflag';
    if (!window.confirm(`Are you sure you want to ${label} this project?`)) return;

    store.server
      .postApi<ServerResponse<boolean>>('../JudgingCaptain/Flag', {
        projectId: project.projectIntId,
        flagged: next,
      })
      .then((x) => {
        if (x.Success) {
          load();
        } else {
          toast.error(x.Message || 'Could not update flag.', { autoClose: false });
        }
      });
  };

  const removeAssignment = (assignmentId: number) => {
    if (!window.confirm('Are you sure you want to remove this assignment?')) return;

    store.server
      .postApi<ServerResponse<boolean>>('../JudgingCaptain/RemoveAssignment', { assignmentId })
      .then((x) => {
        if (x.Success) {
          load();
        } else {
          toast.error(x.Message || 'Could not remove assignment.', { autoClose: false });
        }
      });
  };

  const removeAllForJudge = (judge: ICaptainPortalJudge) => {
    const rows = assignmentsByJudge[judge.personId] ?? [];
    if (!rows.length) return;
    if (!window.confirm('Are you sure you want to remove all of these assignments?')) return;

    store.server
      .postApi<ServerResponse<boolean>>('../JudgingCaptain/RemoveAssignments', {
        assignments: rows.map((r) => r.assignmentId),
      })
      .then((x) => {
        if (x.Success) {
          load();
        } else {
          toast.error(x.Message || 'Could not remove assignments.', { autoClose: false });
        }
      });
  };

  const saveAssignment = () => {
    if (projectKey === '0') {
      toast.warning('You must select a project/entry.');
      return;
    }
    if (judgeId === '0') {
      toast.warning('You must select a judge.');
      return;
    }

    setSaving(true);
    store.server
      .postApi<ServerResponse<boolean>>('../JudgingCaptain/AddAssignment', {
        projectId: projectKey,
        judgeId: Number(judgeId),
        sessionId: Number(sessionId) || 0,
      })
      .then((x) => {
        if (x.Success) {
          toast.info('Assignment saved.');
          setShowAdd(false);
          setProjectKey('0');
          setJudgeId('0');
          setSessionId('0');
          load();
        } else {
          toast.error(x.Message || 'Could not save assignment.', { autoClose: false });
        }
      })
      .finally(() => setSaving(false));
  };

  if (loading && !data) {
    return (
      <div className="captain-portal-page">
        <div className="captain-portal-muted">Loading captain portal…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="captain-portal-page">
        <div className="captain-portal-empty">
          You do not have access to the category captain portal, or no data is available.
        </div>
      </div>
    );
  }

  const projects = data.projects ?? [];
  const judges = data.judges ?? [];
  const sessions = data.sessions ?? [];

  return (
    <div className="captain-portal-page">
      <div className="captain-portal-header">
        <h2>
          <Icon type={IconType.gavel} /> Category Captain Portal
        </h2>
        <p>Entries, judges, and assignment status for your categories.</p>
      </div>

      <Tabs>
        <TabList>
          <Tab>Assignments</Tab>
          <Tab>Judges</Tab>
        </TabList>

        <TabPanel>
          <div className="captain-portal-toolbar">
            <button type="button" className="btn btn-success btn-sm" onClick={() => setShowAdd(true)}>
              <i className="fa fa-plus" /> Add Assignment
            </button>
            <button type="button" className="btn btn-default btn-sm" onClick={() => load()} disabled={loading}>
              <i className="fa fa-refresh" /> Refresh
            </button>
          </div>

          {projects.length === 0 ? (
            <div className="captain-portal-empty">No entries in your categories yet.</div>
          ) : (
            <div className="table-responsive">
              <table className="captain-portal-table table table-striped">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Entry</th>
                    <th style={{ width: 40 }} />
                    <th>Title</th>
                    <th style={{ width: 70 }}>Avg</th>
                    <th style={{ width: 70 }}>Score</th>
                    <th style={{ width: 160 }} className="text-right">
                      Judge
                    </th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => {
                    const rows = assignmentsByProject[project.projectIntId] ?? [];
                    return (
                      <React.Fragment key={`p-${project.projectIntId}`}>
                        <tr>
                          <td rowSpan={rows.length + 1}>
                            <div className="captain-portal-project-id">{project.projectId}</div>
                            <div className="captain-portal-muted">{project.divisionName}</div>
                          </td>
                          <td rowSpan={rows.length + 1}>
                            <button
                              type="button"
                              className={`captain-portal-flag ${project.flagged ? 'flagged' : 'unflagged'}`}
                              title={
                                project.flagged
                                  ? 'Project flagged by judging captain. Click to unflag.'
                                  : 'Click to flag.'
                              }
                              onClick={() => toggleFlag(project)}
                            >
                              <i className={`fa ${project.flagged ? 'fa-flag-checkered' : 'fa-flag-o'}`} />
                            </button>
                          </td>
                          <td rowSpan={rows.length + 1}>
                            <i>{project.title}</i>
                          </td>
                          <td rowSpan={rows.length + 1}>
                            {project.avgScore != null ? project.avgScore.toFixed(2) : ''}
                          </td>
                          <td />
                          <td />
                          <td />
                        </tr>
                        {rows.map((a) => (
                          <tr key={`a-${a.assignmentId}`}>
                            <td>{formatScore(a.score)}</td>
                            <td className="text-right">{a.judgeName}</td>
                            <td className="captain-portal-actions">
                              <button
                                type="button"
                                title="Remove assignment"
                                onClick={() => removeAssignment(a.assignmentId)}
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabPanel>

        <TabPanel>
          {judges.length === 0 ? (
            <div className="captain-portal-empty">No judges found for your categories.</div>
          ) : (
            <div className="table-responsive">
              <table className="captain-portal-table table table-striped">
                <thead>
                  <tr>
                    <th>Judge</th>
                    <th>Assignments</th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {judges.map((j) => {
                    const rows = assignmentsByJudge[j.personId] ?? [];
                    return (
                      <tr key={j.personId}>
                        <td>
                          {j.firstName} {j.lastName}
                          <div className="captain-portal-judge-meta">
                            {j.email ? <div className="small">{j.email}</div> : null}
                            {j.phone ? <div className="small">{j.phone}</div> : null}
                            {j.highestLvlOfEducation ? (
                              <div className="small">{j.highestLvlOfEducation}</div>
                            ) : null}
                            {j.judgeCaptainWilling ? (
                              <div className="small">Willing to be lead judge</div>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          {rows.length > 0 ? (
                            <table className="captain-portal-nested">
                              <tbody>
                                {rows.map((a) => (
                                  <tr key={a.assignmentId}>
                                    <td>
                                      {a.score != null ? (
                                        <span
                                          className="captain-portal-score-ok"
                                          title={`Score: ${formatScore(a.score)}`}
                                        >
                                          <i className="fa fa-check-square-o" />
                                        </span>
                                      ) : null}
                                    </td>
                                    <td className="captain-portal-actions">
                                      <button
                                        type="button"
                                        title="Remove assignment"
                                        onClick={() => removeAssignment(a.assignmentId)}
                                      >
                                        <i className="fa fa-trash" />
                                      </button>
                                    </td>
                                    <td>{a.projectId}</td>
                                    <td>{a.title}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : null}
                        </td>
                        <td className="captain-portal-actions">
                          {rows.length > 0 ? (
                            <button
                              type="button"
                              title="Remove all assignments"
                              onClick={() => removeAllForJudge(j)}
                            >
                              <i className="fa fa-trash" />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabPanel>
      </Tabs>

      {showAdd && (
        <Modal setModalOpen={setShowAdd} title="Add Judging Assignment" size="m">
          <div className="captain-portal-form">
            <div className="form-group">
              <label htmlFor="captain-project">Project / Entry</label>
              <select
                id="captain-project"
                className="form-control"
                value={projectKey}
                onChange={(e) => setProjectKey(e.target.value)}
              >
                <option value="0">Select…</option>
                {[...projects]
                  .sort((a, b) => (a.projectId || '').localeCompare(b.projectId || ''))
                  .map((p) => (
                    <option key={p.projectKey} value={p.projectKey}>
                      {p.projectId} - {p.title}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="captain-session">Session</label>
              <select
                id="captain-session"
                className="form-control"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                <option value="0">Select…</option>
                {sessions.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="captain-judge">Judge</label>
              <select
                id="captain-judge"
                className="form-control"
                value={judgeId}
                onChange={(e) => setJudgeId(e.target.value)}
              >
                <option value="0">Select…</option>
                {judges.map((j) => (
                  <option key={j.personId} value={String(j.personId)}>
                    {j.firstName} {j.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="captain-portal-toolbar">
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveAssignment}
                disabled={saving}
              >
                <i className="fa fa-save" /> Save
              </button>
              <button type="button" className="btn btn-default" onClick={() => setShowAdd(false)}>
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CategoryCaptainPortal;
